import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
} from 'react';

/**
 * The seam between two panes, made draggable.
 *
 * Pointer events rather than mouse events, so a trackpad, a pen and a touch
 * screen all work from one code path, and pointer capture rather than
 * window-level listeners, so a fast drag that leaves the 6px target does not
 * drop the gesture.
 *
 * It is a `separator` with arrow keys as well as a drag: the width of the pane
 * you write prompts in is a real preference, and a preference reachable only by
 * dragging a hairline is not reachable by everyone.
 */
export function PaneResizer({
  label,
  width,
  min,
  max,
  /** Which side of the handle the pane being sized is on. */
  edge,
  reset,
  onChange,
}: {
  label: string;
  width: number;
  min: number;
  max: number;
  edge: 'left' | 'right';
  /** Width a double-click returns to — the screen's own default, not a global one. */
  reset?: number;
  onChange: Dispatch<SetStateAction<number>>;
}) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(max, Math.max(min, Math.round(value))),
    [min, max],
  );

  // While a drag is live the cursor belongs to the drag, not to whatever it
  // happens to be passing over — otherwise it flickers into text carets over
  // the panes either side.
  useEffect(() => {
    if (!dragging) return;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = previous;
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  return (
    <div
      className="pane-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-dragging={dragging}
      onDoubleClick={() => onChange(clamp(reset ?? (edge === 'left' ? 232 : 400)))}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        start.current = { x: event.clientX, width };
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (!start.current) return;
        const delta = event.clientX - start.current.x;
        onChange(clamp(start.current.width + (edge === 'left' ? delta : -delta)));
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        start.current = null;
        setDragging(false);
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 48 : 16;
        // From the previous width rather than the rendered one: a held arrow key
        // fires faster than React re-renders, and reading the prop would make
        // every repeat after the first a no-op.
        if (event.key === 'ArrowLeft') {
          onChange((previous) => clamp(previous + (edge === 'left' ? -step : step)));
        } else if (event.key === 'ArrowRight') {
          onChange((previous) => clamp(previous + (edge === 'left' ? step : -step)));
        } else return;
        event.preventDefault();
      }}
    />
  );
}

/**
 * A pane width that survives a reload.
 *
 * Layout preferences are per-browser, not per-account: they are about the
 * screen you are sitting at, so they live in `localStorage` rather than with
 * the deployment's own configuration.
 */
export function usePaneWidth(
  key: string,
  fallback: number,
): [number, Dispatch<SetStateAction<number>>] {
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? stored : fallback;
  });

  useEffect(() => {
    localStorage.setItem(key, String(width));
  }, [key, width]);

  return [width, setWidth];
}

/** Whether a media query matches, kept current as the window is resized. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    setMatches(media.matches);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Whether the rail is icons-only, as a preference that survives a reload.
 *
 * Three states rather than two: a stored yes, a stored no, and nothing stored
 * yet. The default when nothing is stored follows the window — below 1024px a
 * laptop starts collapsed, because at that width the rail's 208px is what the
 * Work board's fifth column and the builder's inspector were fighting over.
 * Only an explicit toggle writes to `localStorage`. Writing the default back
 * would freeze it: somebody who opened the studio once in a narrow window and
 * later docked a monitor would find the rail still collapsed with nothing
 * they did to make it so.
 */
export function useRailCollapsed(): [boolean, () => void] {
  const narrow = useMediaQuery('(max-width: 1023px)');
  const [stored, setStored] = useState<boolean | null>(() => {
    try {
      const value = localStorage.getItem('reef.rail.collapsed');
      return value === 'true' ? true : value === 'false' ? false : null;
    } catch {
      return null;
    }
  });
  const collapsed = stored ?? narrow;
  const toggle = useCallback(() => {
    const next = !collapsed;
    setStored(next);
    try {
      localStorage.setItem('reef.rail.collapsed', String(next));
    } catch {
      // A browser that refuses storage still gets the toggle for this load.
    }
  }, [collapsed]);
  return [collapsed, toggle];
}

/**
 * The list column of a two-pane screen, made draggable.
 *
 * Almost every screen here is a list beside a detail, and the width that suits
 * the list is a property of what is in it: a connection name carrying two
 * identity badges needs room a policy id does not, and at the fixed 232px they
 * all shared, the badges were clipped mid-word. Handing back the body's props
 * along with the handle keeps the two halves together — a screen cannot mount
 * the resizer and forget to widen its grid, which would leave the handle
 * dragging a seam that is not there.
 */
export function useSidebarPane(key: string, fallback = 232) {
  const [width, setWidth] = usePaneWidth(key, fallback);
  return {
    /** Spread onto the `.body` element. */
    body: {
      className: 'body resizable',
      style: { '--sidebar-w': `${width}px` } as CSSProperties,
    },
    /** Render between the `.sidebar` and the `.main`. */
    resizer: (
      <PaneResizer
        label="List width"
        edge="left"
        width={width}
        min={160}
        max={560}
        reset={fallback}
        onChange={setWidth}
      />
    ),
  };
}
